import React, { useState, useEffect } from 'react';
import { 
  UtensilsCrossed, Phone, MapPin, Star, ChevronRight, 
  Users, Calendar, MessageCircle, ChevronDown, 
  Clock, Menu as MenuIcon, X 
} from 'lucide-react';

export default function RestaurantLanding() {
  const [reservation, setReservation] = useState({ name: '', phone: '', date: '', guests: 2, notes: '' });
  const [notify, setNotify] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!notify) return;
    const t = setTimeout(() => setNotify(''), 4000);
    return () => clearTimeout(t);
  }, [notify]);

  const DISHES = [
    { 
      id: 'dish-1', 
      name: 'Grilled Atlantic Salmon', 
      details: 'Wild-caught salmon, citrus-butter glaze, organic seasonal greens.',
      price: '$28',
      tags: ['GF', 'Best Seller'],
      img: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      id: 'dish-2', 
      name: 'Herb-Roasted Farm Chicken', 
      details: 'Free-range chicken, rosemary & garlic infusion, roasted baby potatoes.',
      price: '$24',
      tags: ['GF'],
      img: 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      id: 'dish-3', 
      name: 'Vegan Buddha Bowl', 
      details: 'Quinoa & roasted kale base, tahini dressing, avocado & toasted seeds.',
      price: '$19',
      tags: ['Vegan', 'Healthy'],
      img: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      id: 'dish-4', 
      name: 'Signature Smoked Burger', 
      details: 'Wagyu beef patty, smoked aged cheddar, house caramelized onions.',
      price: '$22',
      tags: ['Chef Choice'],
      img: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      id: 'dish-5', 
      name: 'Truffle Pasta Carbonara', 
      details: 'Handmade tagliatelle, pancetta, pecorino, farm fresh egg yolk.',
      price: '$26',
      tags: [],
      img: 'https://images.unsplash.com/photo-1612874742237-982e9dfaa7b7?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      id: 'dish-6', 
      name: 'Pan-Seared Scallops', 
      details: 'Jumbo scallops, cauliflower purée, crispy prosciutto crumble.',
      price: '$32',
      tags: ['GF'],
      img: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&q=80&w=800' 
    }
  ];

  const FAQS = [
    { q: "Do you offer home delivery?", a: "Yes, we offer premium delivery within Nairobi. Use the WhatsApp button for instant ordering." },
    { q: "Is the meat Halal certified?", a: "Absolutely. All our meat is sourced from certified Halal suppliers with strict quality control." },
    { q: "Do I need a reservation?", a: "Reservations are highly recommended for dinner service and weekends to guarantee your table." },
    { q: "Is there valet parking?", a: "Yes, we provide complimentary valet parking for all our dining guests in our private lot." }
  ];

  const TESTIMONIALS = [
    { name: "John D.", role: "Food Critic", text: "The grilled salmon is simply out of this world. The combination of flavors is something I haven't experienced before.", rating: 5 },
    { name: "Sarah M.", role: "Regular Guest", text: "Perfect ambiance for a date night. The staff was incredibly attentive and the service made everything so easy.", rating: 5 },
    { name: "Mike K.", role: "Local Guide", text: "Best burger I've had in years! The smoked cheddar and caramelized onions are a game changer.", rating: 5 }
  ];

  function submitReservation(e) {
    e.preventDefault();
    if (!reservation.name || !reservation.phone || !reservation.date) {
      setNotify('Please fill in all required fields');
      return;
    }
    setNotify(`Request sent for ${reservation.name}. We'll confirm via text shortly!`);
    setReservation({ name: '', phone: '', date: '', guests: 2, notes: '' });
  }

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="app-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap');

        :root {
          --primary: #000D6B;
          --primary-dark: #000636;
          --accent: #FA8128;
          --accent-hover: #d66a1a;
          --text-dark: #1e293b;
          --text-light: #64748b;
          --bg-off-white: #f8f9fa;
          --white: #ffffff;
          --shadow-sm: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          --shadow-md: 0 10px 15px -3px rgba(0, 13, 107, 0.08);
          --shadow-lg: 0 20px 25px -5px rgba(0, 13, 107, 0.1);
        }

        * { box-sizing: border-box; }

        body {
          margin: 0;
          font-family: 'Inter', sans-serif;
          color: var(--text-dark);
          background-color: var(--bg-off-white);
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, h4, .font-serif {
          font-family: 'Playfair Display', serif;
        }

        .flex { display: flex; }
        .items-center { align-items: center; }
        .justify-center { justify-content: center; }
        .gap-2 { gap: 0.5rem; }
        .gap-4 { gap: 1rem; }
        .gap-6 { gap: 1.5rem; }
        .flex-wrap { flex-wrap: wrap; }
        .mb-6 { margin-bottom: 1.5rem; }
        .text-3xl { font-size: 1.875rem; line-height: 2.25rem; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .animate-fade-up { animation: fadeUp 0.8s ease-out forwards; }

        .navbar {
          position: fixed;
          top: 0; width: 100%;
          z-index: 1000;
          transition: all 0.3s ease;
          padding: 1.5rem 0;
        }
        .navbar.scrolled {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          padding: 0.8rem 0;
          box-shadow: var(--shadow-sm);
        }
        .nav-content {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo-text {
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          transition: color 0.3s;
        }
        .nav-links { display: flex; gap: 2rem; align-items: center; }
        .nav-btn {
          background: none; border: none;
          font-size: 0.95rem; font-weight: 500;
          cursor: pointer; transition: color 0.2s;
        }
        
        .mobile-menu {
          position: fixed; top: 0; left: 0; width: 100%; height: 100vh;
          background: var(--white);
          z-index: 999;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 2rem;
          transform: translateY(-100%);
          transition: transform 0.4s ease;
        }
        .mobile-menu.open { transform: translateY(0); }

        .hero {
          position: relative;
          height: 100vh;
          min-height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          overflow: hidden;
        }
        .hero-bg {
          position: absolute; inset: 0;
          background-image: url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1600&q=80');
          background-size: cover;
          background-position: center;
        }
        .hero-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, rgba(0, 13, 107, 0.8), rgba(0, 13, 107, 0.4));
        }
        .hero-content {
          position: relative; z-index: 10;
          color: white; padding: 0 1.5rem;
          max-width: 800px;
        }
        .badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.85rem; letter-spacing: 0.05em;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
        }
        .hero-title {
          font-size: clamp(3rem, 6vw, 5rem);
          line-height: 1.1; margin: 0 0 1.5rem 0;
          text-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .hero-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 2.5rem;
          line-height: 1.6;
        }
        .btn {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 1rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none; cursor: pointer;
          text-decoration: none;
        }
        .btn-primary {
          background: var(--accent); color: white;
          box-shadow: 0 4px 15px rgba(250, 129, 40, 0.4);
        }
        .btn-primary:hover {
          background: var(--accent-hover);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(250, 129, 40, 0.5);
        }
        .btn-outline {
          background: transparent; color: white;
          border: 2px solid rgba(255,255,255,0.3);
        }
        .btn-outline:hover {
          border-color: white; background: rgba(255,255,255,0.1);
        }

        .section { padding: 5rem 1.5rem; }
        .bg-white { background: white; }
        .container { max-width: 1200px; margin: 0 auto; }
        .section-header { text-align: center; margin-bottom: 4rem; }
        .section-title {
          font-size: 2.5rem; color: var(--primary);
          margin: 0 0 1rem 0;
        }
        .section-desc {
          color: var(--text-light); max-width: 600px; margin: 0 auto;
        }

        .menu-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
        }
        .dish-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          transition: all 0.4s ease;
          border: 1px solid rgba(0,0,0,0.03);
          display: flex; flex-direction: column;
        }
        .dish-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--shadow-lg);
        }
        .dish-img-wrapper {
          height: 240px; overflow: hidden; position: relative;
        }
        .dish-img {
          width: 100%; height: 100%; object-fit: cover;
          transition: transform 0.6s ease;
        }
        .dish-card:hover .dish-img { transform: scale(1.08); }
        .price-tag {
          position: absolute; bottom: 1rem; right: 1rem;
          background: white; color: var(--primary);
          padding: 0.5rem 1rem;
          border-radius: 8px; font-weight: 700;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }
        .dish-content { padding: 1.5rem; flex-grow: 1; display: flex; flex-direction: column; }
        .dish-name { font-size: 1.25rem; margin: 0 0 0.5rem 0; color: var(--primary); }
        .dish-tags { display: flex; gap: 0.5rem; margin-bottom: 0.8rem; }
        .dish-tag {
          font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em;
          padding: 0.2rem 0.6rem; background: #eef2ff; color: var(--primary);
          border-radius: 4px; font-weight: 600;
        }
        .dish-details { color: var(--text-light); font-size: 0.95rem; line-height: 1.6; margin-bottom: 1.5rem; flex-grow: 1; }
        
        .action-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: auto; }
        .btn-card {
          padding: 0.8rem; border-radius: 8px;
          font-size: 0.9rem; font-weight: 600;
          display: flex; justify-content: center; align-items: center; gap: 0.5rem;
          text-decoration: none; transition: all 0.2s;
        }
        .btn-call { background: #f1f5f9; color: var(--primary); }
        .btn-call:hover { background: #e2e8f0; }
        .btn-order { background: var(--primary); color: white; }
        .btn-order:hover { opacity: 0.9; }

        .reservation-section {
          background-color: var(--primary);
          color: white;
          padding: 6rem 1.5rem;
          position: relative; overflow: hidden;
        }
        .pattern-overlay {
          position: absolute; inset: 0; opacity: 0.05;
          background-image: radial-gradient(var(--white) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .form-container {
          position: relative; z-index: 5;
          background: white;
          border-radius: 24px;
          padding: 3rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          max-width: 1000px; margin: 0 auto;
        }
        .form-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }
        .form-group { display: flex; flex-direction: column; gap: 0.5rem; }
        .form-label {
          color: var(--primary); font-weight: 600; font-size: 0.9rem;
        }
        .form-input, .form-select {
          padding: 1rem; border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          font-family: inherit; font-size: 1rem;
          outline: none; transition: all 0.2s;
        }
        .form-input:focus, .form-select:focus {
          border-color: var(--primary);
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 13, 107, 0.1);
        }
        .form-textarea {
          padding: 1rem; border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          font-family: inherit; font-size: 1rem;
          outline: none; transition: all 0.2s;
          resize: vertical;
          min-height: 100px;
        }
        .form-textarea:focus {
          border-color: var(--primary);
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 13, 107, 0.1);
        }
        .btn-block { width: 100%; margin-top: 1rem; justify-content: center; }

        .split-layout {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 4rem; align-items: start;
        }
        .section-sub-title {
          font-size: 1.875rem;
          margin-bottom: 1.5rem;
          color: var(--primary);
        }
        .faq-item {
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 1.5rem; margin-bottom: 1.5rem;
        }
        .faq-question {
          width: 100%; background: none; border: none;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 1.1rem; font-weight: 600; color: var(--primary);
          cursor: pointer; text-align: left; padding: 0;
        }
        .faq-answer {
          margin-top: 1rem; color: var(--text-light); line-height: 1.6;
          display: none;
        }
        .faq-answer.open { display: block; animation: fadeIn 0.4s; }

        .testimonial-card {
          background: white; padding: 2rem;
          border-radius: 16px; margin-bottom: 1.5rem;
          box-shadow: var(--shadow-sm); border: 1px solid #f1f5f9;
        }
        .stars { color: var(--accent); margin-top: 0.5rem; display: flex; gap: 2px; }
        .quote { font-style: italic; color: var(--text-dark); line-height: 1.7; font-size: 1.05rem; }
        .author { margin-top: 1.5rem; font-weight: 700; color: var(--primary); display: flex; justify-content: space-between; align-items: center; }
        .role { font-weight: 400; font-size: 0.85rem; color: var(--text-light); }

        .toast {
          position: fixed; bottom: 2rem; right: 2rem;
          background: var(--primary); color: white;
          padding: 1rem 1.5rem; border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          display: flex; align-items: center; gap: 1rem;
          z-index: 2000;
          animation: fadeUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .toast-dot { width: 8px; height: 8px; background: var(--accent); border-radius: 50%; }

        @media (max-width: 900px) {
          .split-layout { grid-template-columns: 1fr; gap: 3rem; }
          .hero-title { font-size: 3rem; }
          .form-container { padding: 2rem 1.5rem; }
        }
        @media (max-width: 600px) {
          .nav-links { display: none; }
          .mobile-toggle { display: block; }
          .form-grid { grid-template-columns: 1fr; }
        }
        @media (min-width: 601px) {
          .mobile-toggle { display: none; }
        }
      `}</style>

    

      <header className="hero">
        <div className="hero-bg"></div>
        <div className="hero-overlay"></div>
        <div className="hero-content animate-fade-up">
          <div className="badge">
            <Star size={14} fill="#FA8128" color="#FA8128" />
            <span>Voted Best Ambience 2025</span>
          </div>
          <h1 className="hero-title font-serif">
            Culinary Excellence<br />
            <span style={{ color: 'var(--accent)' }}>Redefined.</span>
          </h1>
          <p className="hero-subtitle">
            Experience a symphony of locally sourced flavors in the heart of Nairobi. 
            Where modern dining meets timeless tradition.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button onClick={() => scrollToSection('menu')} className="btn btn-primary">
              View Our Menu <ChevronRight size={18} />
            </button>
            <button onClick={() => scrollToSection('reserve')} className="btn btn-outline">
              <Calendar size={18} /> Book a Table
            </button>
          </div>
        </div>
      </header>

      <section id="menu" className="section bg-white">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title font-serif">Signature Creations</h2>
            <p className="section-desc">Curated by Executive Chef James, featuring organic ingredients from our local partner farms.</p>
          </div>

          <div className="menu-grid">
            {DISHES.map((dish) => (
              <div key={dish.id} className="dish-card">
                <div className="dish-img-wrapper">
                  <img src={dish.img} alt={dish.name} className="dish-img" />
                  <div className="price-tag">{dish.price}</div>
                </div>
                <div className="dish-content">
                  <div className="dish-tags">
                    {dish.tags.map(tag => <span key={tag} className="dish-tag">{tag}</span>)}
                  </div>
                  <h3 className="dish-name font-serif">{dish.name}</h3>
                  <p className="dish-details">{dish.details}</p>
                  <div className="action-row">
                    <a href="tel:0754906060" className="btn-card btn-call">
                      <Phone size={16} /> Call
                    </a>
                    <a href="https://wa.me/254754906060" className="btn-card btn-order">
                      <MessageCircle size={16} /> Order
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="reserve" className="reservation-section">
        <div className="pattern-overlay"></div>
        <div className="container">
          <div className="section-header" style={{ color: 'white', marginBottom: '3rem' }}>
            <h2 className="section-title font-serif" style={{ color: 'white' }}>Reserve Your Table</h2>
            <p className="section-desc" style={{ color: 'rgba(255,255,255,0.7)' }}>Join us for an unforgettable evening. Reservations recommended for weekends.</p>
          </div>

          <div className="form-container">
            <form onSubmit={submitReservation}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder="e.g. Michael Scott"
                    value={reservation.name}
                    onChange={e => setReservation({...reservation, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-input" 
                    placeholder="07XX XXX XXX"
                    value={reservation.phone}
                    onChange={e => setReservation({...reservation, phone: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Date & Time</label>
                  <input 
                    type="datetime-local" 
                    className="form-input"
                    value={reservation.date}
                    onChange={e => setReservation({...reservation, date: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Party Size</label>
                  <select 
                    className="form-select"
                    value={reservation.guests}
                    onChange={e => setReservation({...reservation, guests: parseInt(e.target.value)})}
                  >
                    {[2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                      <option key={n} value={n}>{n} Guests</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label className="form-label">Special Requests (Optional)</label>
                <textarea
                  className="form-textarea"
                  placeholder="Dietary restrictions, seating preferences, special occasions..."
                  value={reservation.notes}
                  onChange={e => setReservation({...reservation, notes: e.target.value})}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block">
                <Calendar size={18} />
                Confirm Reservation Request
              </button>
            </form>
          </div>
        </div>
      </section>

      <section id="reviews" className="section">
        <div className="container">
          <div className="split-layout">
            
            <div>
              <h3 className="font-serif section-sub-title">Frequently Asked</h3>
              {FAQS.map((item, i) => (
                <div key={i} className="faq-item">
                  <button className="faq-question" onClick={() => toggleFaq(i)}>
                    {item.q}
                    <ChevronDown 
                      size={20} 
                      style={{ 
                        transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)', 
                        transition: 'transform 0.3s' 
                      }} 
                      color="#FA8128" 
                    />
                  </button>
                  <div className={`faq-answer ${openFaq === i ? 'open' : ''}`}>
                    {item.a}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <h3 className="font-serif section-sub-title">Guest Stories</h3>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="testimonial-card">
                  <p className="quote">"{t.text}"</p>
                  <div className="author">
                    <div>
                      {t.name}
                      <div className="role">{t.role}</div>
                    </div>
                    <div className="stars">
                      {[...Array(t.rating)].map((_, idx) => (
                        <Star key={idx} size={14} fill="#FA8128" color="#FA8128" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {notify && (
        <div className="toast">
          <div className="toast-dot"></div>
          {notify}
        </div>
      )}
    </div>
  );
}